// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getDatabase, ref, get, remove } from "firebase/database";
import { getAuth } from "firebase/auth";
import { getStorage } from "firebase/storage";
// Auth-domain functions moved to authService — imported here for internal use within
// initializeDefaultData (which calls createUser). The re-export shims below handle
// backwards-compatible external callers.
import { createUser } from './services/authService';
// Time-record functions moved to timeService — imported here for internal use within
// initializeDefaultData (which calls createTimeRecord). The re-export shims below handle
// backwards-compatible external callers.
import { createTimeRecord } from './services/timeService';
// Archive functions moved to archiveService — imported here for internal use within
// deleteUser (which calls archiveEmployeeData). The re-export shims below handle
// backwards-compatible external callers.
import { archiveEmployeeData } from './services/archiveService';
// Import company utilities
import { detectUserCompany, getUsersPath } from './utils/companyUtils';
// Time rounding utilities moved to timeService — imports removed from firebase.js
// (applyTimeRounding, applyTimestampRounding, ceilTimestampToQuarterHour, formatLocalYMD)

// Dev-only logger — no-op in production builds.
const dbg = process.env.NODE_ENV !== 'production' ? console.log.bind(console) : () => {};

// Your web app's Firebase configuration
dbg('🔍 Debug: Environment Variables Check');
dbg('API Key:', process.env.REACT_APP_FIREBASE_API_KEY ? 'Found' : 'Missing');
dbg('Auth Domain:', process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ? 'Found' : 'Missing');
dbg('Database URL:', process.env.REACT_APP_FIREBASE_DATABASE_URL ? 'Found' : 'Missing');
dbg('Current origin:', window.location.origin);

const firebaseConfig = {
  apiKey: "AIzaSyDkWg8aJj6hw7WVRwTns3FdF4AWVAfGpcw",
  authDomain: "punch-c0c95.firebaseapp.com",
  databaseURL: "https://punch-c0c95-default-rtdb.firebaseio.com",
  projectId: "punch-c0c95",
  storageBucket: "punch-c0c95.firebasestorage.app",
  messagingSenderId: "1046344147129",
  appId: "1:1046344147129:web:74b5f35ad184fb20b0fb68",
  measurementId: "G-EDZBJC93SE"
};


dbg('🔥 Firebase Config:', {
  apiKey: firebaseConfig.apiKey ? '✓ Set' : '✗ Missing',
  authDomain: firebaseConfig.authDomain,
  databaseURL: firebaseConfig.databaseURL,
  projectId: firebaseConfig.projectId
});

let app;
let auth;
let database;
let storage;

// Initialize Firebase lazily with error handling for localhost
const initializeFirebase = () => {
  if (!app) {
    try {
      app = initializeApp(firebaseConfig);
      auth = getAuth(app);
      database = getDatabase(app);
      storage = getStorage(app);
      
      dbg('✅ Firebase initialized successfully');
      dbg('🌍 Running on:', window.location.origin);
      
      // Log auth state for debugging
      auth.onAuthStateChanged((user) => {
        if (user) {
          dbg('👤 User authenticated:', user.email);
        } else {
          dbg('👤 No user authenticated');
        }
      });
      
    } catch (error) {
      console.error('❌ Firebase initialization error:', error);
      console.error('This might be a CORS or configuration issue.');
      console.error('Make sure localhost:3001 is added to Firebase authorized domains.');
      throw error;
    }
  }
  return { app, auth, database, storage };
};

// Export a function to get Firebase instances
export const getFirebase = () => {
  return initializeFirebase();
};

// Export individual instances
export const getAuthInstance = () => {
  return initializeFirebase().auth;
};

export const getDatabaseInstance = () => {
  return initializeFirebase().database;
};

// Storage accessor — used by fileService; added here to keep all Firebase
// instance accessors co-located. Do NOT add business logic to this file.
export const getStorageInstance = () => {
  return initializeFirebase().storage;
};

// Database structure:
// Legacy (existing company - UNTOUCHED):
// /users/{userId}
// /timeRecords/{recordId}
// /employeeStatus/{employeeId}
// /settings/{settingKey}
//
// New multi-company structure:
// /companies/{companyId}/users/{userId}
// /companies/{companyId}/timeRecords/{recordId}
// /companies/{companyId}/employeeStatus/{employeeId}
// /companies/{companyId}/settings/{settingKey}
// /userCompanyMappings/{userId} - Maps users to companies

// User Management - Company Aware
// TODO remove shim — refactor:authService
export { createUser } from './services/authService';

export const getUser = async (userId) => {
  // Use detectUserCompany which already searches company-specific paths
  // No fallback to legacy /users path - data should be in company path
  const companyInfo = await detectUserCompany(userId);

  if (companyInfo) {
    return companyInfo.userData;
  }

  console.warn(`User ${userId} not found in any company path`);
  return null;
};

// Company-aware function to get all users for a specific company
export const getAllUsers = async (companyId = null) => {
  const { database } = getFirebase();

  dbg('getAllUsers called with companyId:', companyId);

  // Default to legacy company if not specified
  const targetCompanyId = companyId || 'legacy';

  // Always use company-specific path
  const usersPath = getUsersPath(targetCompanyId);
  dbg('Using company-specific users path:', usersPath);
  const usersRef = ref(database, usersPath);
  const snapshot = await get(usersRef);
  const users = snapshot.val() || {};
  dbg('Found users in company path:', Object.keys(users).length);
  return users;
};

// Time Records Management - Company Aware
// TODO remove shim — refactor:timeService
export {
  createTimeRecord,
  getTimeRecords,
  correctTimeRecord,
  updateTimeRecord,
  updateTimeRecordApproval,
  getApprovalStatus,
  bulkUpdateApprovals,
} from './services/timeService';

// Employee Status Management - Company Aware
// TODO remove shim — refactor:timeService
export {
  updateEmployeeStatus,
  getEmployeeStatus,
  getAllEmployeeStatuses,
} from './services/timeService';

// Initialize default data
export const initializeDefaultData = async () => {
  const defaultUsers = [
    {
      id: '1',
      name: 'John Doe',
      email: 'john@example.com',
      password: 'password123',
      role: 'user',
      status: 'active'
    },
    {
      id: '2',
      name: 'Jane Smith',
      email: 'jane@example.com',
      password: 'password123',
      role: 'admin',
      status: 'active'
    },
    {
      id: '3',
      name: 'Mike Johnson',
      email: 'mike@example.com',
      password: 'password123',
      role: 'admin',
      status: 'active'
    }
  ];

  // Create default users
  for (const user of defaultUsers) {
    await createUser(user);
  }

  // Create some sample time records
  const sampleRecords = [
    {
      employeeId: '1',
      action: 'clock-in',
      location: 'Main Office',
      notes: 'Regular start'
    },
    {
      employeeId: '1',
      action: 'lunch-out',
      location: 'Main Office',
      notes: 'Lunch break'
    },
    {
      employeeId: '1',
      action: 'lunch-in',
      location: 'Main Office',
      notes: 'Back from lunch'
    },
    {
      employeeId: '1',
      action: 'clock-out',
      location: 'Main Office',
      notes: 'Regular end'
    }
  ];

  // Create sample records with timestamps spread across the current week
  const now = new Date();
  for (let i = 0; i < sampleRecords.length; i++) {
    const record = sampleRecords[i];
    const timestamp = new Date(now);
    timestamp.setDate(now.getDate() - i); // Spread across different days
    timestamp.setHours(9 + i); // Different hours
    
    await createTimeRecord({
      ...record,
      timestamp: timestamp.toISOString()
    });
  }
};

// TODO remove shim — refactor:authService
export { updateUserProfile } from './services/authService';

// Delete user from database
// Archive employee data and time records before deletion - REMOVED: Using comprehensive version below

// ============================================================================
// ARCHIVE WORKFLOW
// TODO remove shims — refactor:archiveService
// ============================================================================
export {
  startArchiveEmployee,
  completeArchiveEmployee,
  getPendingArchiveEmployees,
  cancelArchiveEmployee,
  archiveEmployeeData,
  unarchiveEmployee,
  getAllArchivedEmployees,
  getArchivedEmployeeData,
} from './services/archiveService';

export const deleteUser = async (userId, companyId = null, archiveData = true) => {
  try {
    const { database } = getFirebase();

    // Archive employee data first if requested
    if (archiveData) {
      await archiveEmployeeData(userId, companyId);
    }

    // Determine the correct path based on company
    const userPath = companyId && companyId !== 'legacy'
      ? `companies/${companyId}/users/${userId}`
      : `users/${userId}`;

    dbg('Deleting user at path:', userPath);

    const userRef = ref(database, userPath);

    // Check if user exists first
    const snapshot = await get(userRef);
    if (!snapshot.exists()) {
      throw new Error('User not found');
    }

    // Delete the user
    await remove(userRef);

    dbg('User deleted successfully (time records preserved in archive)');
    return true;

  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// ============================================================================
// WORK LOCATIONS MANAGEMENT
// TODO remove shims — refactor:workLocationService
// ============================================================================
export {
  createWorkLocation,
  listWorkLocations,
  getWorkLocations,
  updateWorkLocation,
  deleteWorkLocation,
} from './services/workLocationService';

// ============================================================================
// SCHEDULES MANAGEMENT
// TODO remove shims — refactor:scheduleService
// ============================================================================
export {
  createSchedule,
  getSchedules,
  updateSchedule,
  deleteSchedule,
  assignEmployeesToSchedule,
} from './services/scheduleService';

// ============================================================================
// MISSED CLOCK-OUT DETECTION
// TODO remove shims — refactor:timeService
// ============================================================================
export {
  detectMissedClockOuts,
  getMissedClockOuts,
  resolveMissedClockOut,
} from './services/timeService';

// ============================================================================
// EMPLOYEE GROUPS MANAGEMENT
// TODO remove shims — refactor:workLocationService
// ============================================================================
export {
  createEmployeeGroup,
  getEmployeeGroups,
  updateEmployeeGroup,
  deleteEmployeeGroup,
  updateEmployeeGroup_Assignment,
} from './services/workLocationService';